import { useState, useCallback } from 'react';
import API_ROUTES from './apiRoutes';

interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
}

export const useAuth = () => {
  // Inicializar estado diretamente do localStorage (síncrono) para evitar redirects prematuros
  const [authState, setAuthState] = useState<AuthState>(() => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    
    return {
      isAuthenticated: !!accessToken,
      accessToken,
      refreshToken,
    };
  });

  const login = (accessToken: string, refreshToken: string) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    
    setAuthState({
      isAuthenticated: true,
      accessToken,
      refreshToken,
    });
  };

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
    
    setAuthState({
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
    });
  }, []);


  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      logout();
      return null;
    }

    try {
      const refreshUrl = API_ROUTES.AUTH.refresh(refreshToken);
      
      const response = await fetch(refreshUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        logout();
        return null;
      }

      const result = await response.json();
      const newAccessToken = result.access_token;
      
      if (!newAccessToken) {
        logout();
        return null;
      }
      
      localStorage.setItem('accessToken', newAccessToken);
      
      setAuthState(prev => ({
        ...prev,
        accessToken: newAccessToken,
      }));

      return newAccessToken;
    } catch (error) {
      logout();
      return null;
    }
  }, [logout]);

  return {
    ...authState,
    login,
    logout,
    refreshAccessToken,
  };
};
